/**
 * Tablero.tsx — la pantalla de inicio, con los números del libro.
 *
 * Era `dashboard.html` de la Fase 0 y hasta hoy seguía siéndolo: las tarjetas,
 * la gráfica, la ecuación contable y la exposición cambiaria salían de
 * `src/datos/muestra.ts`. **Cifras inventadas con la misma cara que las de
 * verdad, en lo primero que se ve al entrar.** Ahora vienen de la base.
 *
 * ── ▶ NADA DE ESTA PANTALLA SE VUELVE A CALCULAR ──────────────────────────
 *
 * Los totales salen de `control.tablero`, que a su vez llama a
 * `balance_de_situacion` y a `estado_de_resultados` — los mismos que dibujan
 * los reportes. Si el tablero sumara por su cuenta, las dos pantallas podrían
 * decir cifras distintas de la misma empresa, y quien mira el tablero no tiene
 * cómo saber cuál vale.
 *
 * Acá no hay una sola suma de dinero. La única aritmética es la **variación
 * porcentual** contra el mes anterior, que no es dinero: es una razón entre dos
 * importes, y solo se calcula cuando hay mes anterior de verdad.
 *
 * ── Lo que se conserva del mockup, y por qué ──────────────────────────────
 *
 * 1. **La gráfica la dibuja recharts.** Lo que convierte a número es geometría
 *    y pasa por `paraDibujar`. Las etiquetas —incluido el tooltip— salen de
 *    `monto()`, sobre el texto.
 *
 * 2. **`isAnimationActive={false}` no es una preferencia de estilo.** recharts
 *    anima las barras DESDE CERO, y si la animación no arranca la gráfica se
 *    queda dibujando ceros: datos falsos en pantalla, con la leyenda del eje al
 *    lado haciéndolos creíbles.
 *
 * 3. **La ecuación contable se comprueba, no se afirma.** Quien decide si
 *    cuadra es `cuadre_de_los_estados`. Un tablero que dice «libros cuadrados»
 *    pase lo que pase no informa: decora.
 */
import { useEffect, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import { PanelDeIndices } from '../componentes/PanelDeIndices.tsx';
import { PuestaEnMarcha } from '../componentes/PuestaEnMarcha.tsx';
import { Texto, useIdioma } from '../nucleo/idioma.tsx';
import { useSesion } from '../nucleo/sesion.tsx';
import { paraDibujar, sumar, type Importe } from '../nucleo/dinero.ts';
import { Saldos } from '../componentes/Saldos.tsx';
import { anioYMesDeHoy, diasQueQuedanDelMes } from '../nucleo/hoy.ts';
import {
  traerTablero, FalloDeApi,
  type CuentaExpuesta, type PuntoDeLaSerie, type ResumenDelTablero,
} from '../datos/libros.ts';

/* ═══════════════════════════════════════════════════════════════════════════
   Las tarjetas
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * La variación contra el mes anterior, en porcentaje.
 *
 * ▶ Devuelve `null` cuando NO SE PUEDE calcular, y son dos casos distintos que
 * dan la misma respuesta a propósito: que no haya mes anterior en el ejercicio,
 * y que el mes anterior fuera cero. Un porcentaje sobre cero no es infinito ni
 * es 100: no existe, y dibujarlo como «+100 %» sería inventar una medida.
 *
 * No pasa por `dinero.ts` porque no está sumando dinero: divide dos importes
 * para sacar una razón, que es un número y se presenta como número.
 */
function variacion(ahora: string | null, antes: string | null): number | null {
  // ▶ `ahora` también puede faltar: el efectivo no viene cuando la empresa no
  // tiene ninguna cuenta declarada como tal. Un `?? '0'` acá habría inventado un
  // cero y con él una variación contra el mes pasado.
  if (ahora === null || antes === null) return null;
  const a = Number(antes);
  const b = Number(ahora);
  if (!Number.isFinite(a) || !Number.isFinite(b) || a === 0) return null;
  return ((b - a) / Math.abs(a)) * 100;
}

function Kpi({ rotulo, valor, delta, mesAnterior, tono, sinDatos }: {
  rotulo: string;
  valor: Importe | null;
  delta: number | null;
  /** El nombre del mes con el que se compara. Ver `dash.vsPrev`. */
  mesAnterior: string | null;
  tono?: string;
  sinDatos?: string;
}) {
  const { t, monto } = useIdioma();
  const sube = delta !== null && delta >= 0;

  return (
    <article className="card card--hover">{/* regla-ok: `card--hover` no viste en reposo A PROPÓSITO — su única regla es `.card--hover:hover` y el nombre lo dice; único caso de la casa */}
      <div className="kpi__label">{rotulo}</div>
      {valor === null ? (
        // Ni un cero ni un guion: el motivo. Un cero se lee como que no hubo
        // nada; el motivo dice qué falta para poder calcularlo.
        //
        // ▶ Y con clase PROPIA, no `kpi__value` con modificadores encima.
        // Decía `kpi__value muted small`: las tres pesan igual y lo decide el
        // orden del archivo, así que ganaba `.kpi__value` —que está más
        // abajo— y la frase salía a 26 px en la tipografía del dinero, justo
        // donde sus tres vecinas enseñan un monto. Una explicación con el
        // disfraz de una cifra la lee como cifra quien pasa la vista por
        // encima, que es todo el mundo. La encontró la caminata.
        <div className="kpi__nota">{sinDatos}</div>
      ) : (
        <div className={`kpi__value${tono ?? ''}`}>{monto(valor)}</div>
      )}
      {/* ▶ EL MES CON EL QUE SE COMPARA SE NOMBRA, y sale del calendario.
       *
       * `dash.vsPrev` decía literalmente «vs. julio» —una frase del mockup de
       * la Fase 0 que se quedó en el diccionario—, y en septiembre lo decía
       * comparándose contra agosto. Un mes escrito a mano en un rótulo es un
       * dato inventado: cierto una vez al año y falso las otras once. */}
      {delta !== null && mesAnterior !== null && (
        <div className={`kpi__delta ${sube ? 'kpi__delta--up' : 'kpi__delta--down'}`}>
          {sube ? '▲' : '▼'} {Math.abs(delta).toFixed(1)} %{' '}
          {t('dash.vsPrev', { mes: mesAnterior })}
        </div>
      )}
    </article>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   La gráfica
   ═════════════════════════════════════════════════════════════════════════ */

interface Punto {
  /** Geometría. No se muestra: la etiqueta sale de `textos`. */
  mes: string;
  ingresos: number;
  gastos: number;
  /** Lo que sí se muestra, ya formateado desde el texto original. */
  textos: { ingresos: string; gastos: string };
}

function Grafica({ serie, moneda, anio }: {
  serie: readonly PuntoDeLaSerie[]; moneda: string; anio: number;
}) {
  const { t, monto, idioma } = useIdioma();

  const datos: Punto[] = serie.map((p) => {
    const ingresos: Importe = { valor: p.ingresos, moneda };
    const gastos: Importe = { valor: p.gastos, moneda };
    return {
      mes: new Intl.DateTimeFormat(idioma === 'es' ? 'es-CR' : 'en-US', { month: 'short' })
        .format(new Date(anio, p.mes - 1, 15)),
      ingresos: paraDibujar(ingresos),
      gastos: paraDibujar(gastos),
      textos: { ingresos: monto(ingresos), gastos: monto(gastos) },
    };
  });

  return (
    <article className="card card--pad0">
      <div className="card__head">
        <h3>{t('dash.chart.title')}</h3>
        <span className="spacer" />
        <span className="chip chip--income chip--plain">{t('common.income')}</span>
        <span className="chip chip--expense chip--plain">{t('common.expense')}</span>
      </div>
      <div className="card__body">
        {/* ▶ «SIN MESES» Y «DOCE MESES EN CERO» SON LO MISMO PARA QUIEN MIRA.
         *
         * El vacío solo se dibujaba cuando la serie venía sin puntos. Con una
         * empresa recién creada la serie viene entera —los doce meses del
         * ejercicio, todos en cero— y entonces se pintaba una gráfica de nada:
         * ninguna barra, y un eje vertical con «0 M» repetido cinco veces,
         * porque una escala cuyo máximo es cero no tiene tramos que repartir.
         *
         * Se vio en producción, en la segunda empresa demo, el primer día.
         * Es la misma frase la que sirve para los dos casos —«todavía no hay
         * meses con movimiento»— porque los dos casos son el mismo hecho.
         *
         * Y por eso basta `every`: sobre una lista vacía devuelve `true`, así
         * que la condición dice «no hay ningún mes con movimiento» y cubre las
         * dos formas de que eso pase. */}
        {datos.every((p) => p.ingresos === 0 && p.gastos === 0) ? (
          <p className="muted small">{t('dash.noSeries')}</p>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={datos} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid stroke="var(--border)" vertical={false} />
              <XAxis
                dataKey="mes" tickLine={false} axisLine={{ stroke: 'var(--border-strong)' }}
                tick={{ fill: 'var(--muted)', fontFamily: 'var(--f-mono)', fontSize: 10 }}
              />
              <YAxis
                tickLine={false} axisLine={false} width={54}
                tick={{ fill: 'var(--muted-2)', fontFamily: 'var(--f-mono)', fontSize: 9.5 }}
                // Solo el eje: son millones redondos, no una cifra contable.
                tickFormatter={(v: number) => `${Math.round(v / 100_000) / 10} M`}
              />
              <Tooltip
                cursor={{ fill: 'var(--surface-2)' }}
                contentStyle={{
                  background: 'var(--ink)', border: 0, borderRadius: 8,
                  fontFamily: 'var(--f-mono)', fontSize: 11, color: 'var(--on-dark)',
                }}
                itemStyle={{ color: 'var(--on-dark)' }}
                labelStyle={{ color: 'var(--acc-bright)' }}
                // El tooltip muestra el TEXTO, no el número que dibujó la barra.
                formatter={(_v, nombre, entrada) => {
                  const p = entrada.payload as Punto;
                  const clave = nombre === t('common.income') ? 'ingresos' : 'gastos';
                  return [p.textos[clave], nombre];
                }}
              />
              {/* Ver la cabecera: sin esto, una animación que no arranca deja
                  la gráfica dibujando ceros. */}
              <Bar dataKey="ingresos" name={t('common.income')} fill="var(--income)"
                   radius={[3, 3, 0, 0]} maxBarSize={26} isAnimationActive={false} />
              <Bar dataKey="gastos" name={t('common.expense')} fill="var(--expense)"
                   radius={[3, 3, 0, 0]} maxBarSize={26} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </article>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   La ecuación contable
   ═════════════════════════════════════════════════════════════════════════ */

function EcuacionContable({ resumen, moneda }: {
  resumen: ResumenDelTablero; moneda: string;
}) {
  const { t, monto } = useIdioma();
  const importe = (v: string): Importe => ({ valor: v, moneda });

  return (
    <article className="card">
      <div className="label"><span>{t('dash.equation')}</span></div>
      <p className="small muted" style={{ margin: 'var(--s5) 0 var(--s6)' }}>
        {t('dash.equationSub')}
      </p>
      <table className="tbl tbl--compact">
        <tbody>
          <tr>
            <td>{t('common.asset')}</td>
            <td className="amount debit">{monto(importe(resumen.activo))}</td>
          </tr>
          <tr>
            <td>{t('common.liability')}</td>
            <td className="amount credit">{monto(importe(resumen.pasivo))}</td>
          </tr>
          <tr>
            <td>{t('common.equity')}</td>
            <td className="amount credit">{monto(importe(resumen.patrimonio))}</td>
          </tr>
        </tbody>
      </table>

      {/* ▶ Quien decide si cuadra es `cuadre_de_los_estados`, no una resta de
          acá. Y el cartel solo sale si de verdad cuadra. */}
      {resumen.cuadra ? (
        <div className="alert alert--ok" style={{ marginTop: 'var(--s6)' }}>
          <span>
            <strong>{t('dash.balancedTitle')}</strong>
            {/* Antes decía «en los N asientos del periodo», y la comprobación
                no es sobre los asientos del periodo: es sobre el balance al
                cierre, que arrastra todo lo anterior. Con septiembre en cero
                afirmaba haber cuadrado «los 0 asientos del periodo». */}
            {t('dash.balancedBody')}
          </span>
        </div>
      ) : (
        <div className="alert alert--warn" style={{ marginTop: 'var(--s6)' }}>
          <span><strong>{t('dash.unbalancedTitle')}</strong>{t('dash.unbalancedBody')}</span>
        </div>
      )}
    </article>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Exposición cambiaria
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * El saldo expuesto, en su moneda y como está en libros.
 *
 * ▶ NO DICE CUÁNTO SE GANÓ O SE PERDIÓ, Y NO ES UN OLVIDO.
 *
 * El mockup traía una línea de «ganancia por diferencial». Ese número sale de
 * comparar el saldo contra el tipo del último día del mes, y **quien lo calcula
 * y lo asienta es la revaluación**, en el cierre. Calcularlo acá sería una
 * segunda versión de la misma regla, dibujada, que el día que discrepe de los
 * libros no va a ser la que tenga razón.
 *
 * Lo que sí se puede decir sin calcular nada es qué saldo está expuesto.
 */
function ExposicionCambiaria({ expuestas, moneda }: {
  expuestas: readonly CuentaExpuesta[]; moneda: string;
}) {
  const { t, monto, idioma } = useIdioma();

  /**
   * Cuánto mueve la utilidad un colón de variación, POR MONEDA.
   *
   * ▶ Análisis dimensional, que es lo que hace que esto sea exacto:
   * con `X` unidades de una moneda extranjera, si el tipo sube un colón por
   * unidad, el valor en libros sube `X × 1 colón` = `X` colones. Por eso la
   * suma se hace EN LA MONEDA EXTRANJERA —sumar dólares con dólares— y la
   * cifra resultante se rotula en la moneda local: el factor es 1 colón por
   * unidad, no un tipo de cambio que haya que buscar.
   *
   * Va por moneda y no en una sola cifra: un colón de variación del dólar y
   * uno del euro no son el mismo suceso, y sumarlos daría un número que no
   * corresponde a nada que pueda pasar.
   */
  const sensibilidad = [...new Set(expuestas.map((c) => c.moneda))].flatMap((m) => {
    const enSuMoneda = sumar(expuestas
      .filter((c) => c.moneda === m)
      .map((c) => ({ valor: c.saldo_original, moneda: m })));
    if (enSuMoneda === null) return [];
    return [{ moneda: m, porColon: { valor: enSuMoneda.valor, moneda } }];
  });

  return (
    <article className="card">
      <div className="label"><span>{t('dash.fxTitle')}</span></div>
      <p className="small muted" style={{ margin: 'var(--s5) 0 var(--s6)' }}>{t('dash.fxSub')}</p>

      {expuestas.length === 0 ? (
        <p className="muted small">{t('dash.noFx')}</p>
      ) : (
        <div className="stack" style={{ gap: 'var(--s5)' }}>
          {expuestas.map((c) => (
            <div key={`${c.cuenta}-${c.moneda}`} className="row row--tight">
              <span className="small">
                {c.cuenta} · {idioma === 'es' ? c.nombre : c.nombre_en}
                <span className="cur cur--usd">{c.moneda}</span>
              </span>
              <span className="spacer" />
              <span className="amt2">
                {/* Los dos importes, cada uno con su moneda. El de libros no
                    reemplaza al original: lo acompaña. */}
                <span className="amt2__main">
                  {monto({ valor: c.saldo_original, moneda: c.moneda })}
                </span>
                <span className="amt2__eq">
                  {monto({ valor: c.saldo_en_libros, moneda })}
                </span>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ▶ LA SENSIBILIDAD SE CALCULA, O NO SE DICE.
       *
       * Antes era una frase con un número inventado dentro —«mueve la
       * utilidad en ₡3 420»— escrita en el diccionario desde el mockup, y se
       * enseñaba SIEMPRE: incluso dos líneas debajo de «no hay saldos en
       * moneda extranjera», donde la respuesta correcta es cero.
       *
       * Es el último resto de `datos/muestra.ts`, y sobrevivió a borrarlo
       * porque no vivía en un fixture sino en un TEXTO. Las pruebas bilingües
       * comprueban que cada clave esté en los dos idiomas; no que la frase no
       * afirme un hecho falso. */}
      {sensibilidad.map((s) => (
        <p key={s.moneda} className="xs muted" style={{ margin: 'var(--s6) 0 0' }}>
          {t('dash.fxNote', { moneda: s.moneda, monto: monto(s.porColon) })}
        </p>
      ))}
    </article>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */

export function Tablero() {
  const { t, local } = useIdioma();
  const { pedir, sesion } = useSesion();
  const moneda = sesion?.empresa.monedaLocal ?? '';

  // El mes en curso, en la zona del NEGOCIO. Antes salía de UTC con un
  // comentario que decía evitar justo lo que causaba: la noche del último día
  // del mes, UTC ya es el mes siguiente y el tablero se cambiaba solo.
  const { anio, mes: mesN } = anioYMesDeHoy();

  // Los días que quedan del mes. Sale del calendario y no de un dato guardado:
  // el `4` que decía antes venía del mockup y no se movía nunca.
  const dias = diasQueQuedanDelMes();

  /**
   * El nombre del mes con el que se compara.
   *
   * Se construye con `Date.UTC` a mediodía sobre un año y un mes que ya vienen
   * de la zona del negocio: no es «ahora», es un mes concreto que hay que
   * nombrar, así que el reloj no entra.
   */
  const mesAnterior = new Intl.DateTimeFormat(local, { month: 'long' })
    .format(new Date(Date.UTC(anio, mesN - 2, 15)));


  const [resumen, setResumen] = useState<ResumenDelTablero | null>(null);
  const [serie, setSerie] = useState<readonly PuntoDeLaSerie[]>([]);
  const [expuestas, setExpuestas] = useState<readonly CuentaExpuesta[]>([]);
  const [fallo, setFallo] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);

  /** Hay con qué comparar: el mes tiene asientos y hubo mes anterior. */
  const comparable = (resumen?.asientos ?? 0) > 0 && resumen?.hay_previo === true;

  const empresaId = sesion?.empresa.id ?? null;

  useEffect(() => {
    if (!empresaId) return;
    let vivo = true;
    setCargando(true);
    setFallo(null);
    (async () => {
      try {
        const d = await traerTablero(pedir, anio, mesN);
        if (!vivo) return;
        setResumen(d.resumen);
        setSerie(d.serie);
        setExpuestas(d.expuestas);
      } catch (e) {
        if (!vivo) return;
        setResumen(null);
        const f = e instanceof FalloDeApi ? e : null;
        setFallo(f?.clase === 'conexion' ? 'sb.conexion'
          : f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
  }, [pedir, empresaId, anio, mesN]);

  // Mayúscula solo en la PRIMERA letra. `text-transform: capitalize` la pone en
  // cada palabra, y en español eso da «Agosto De 2026»: el «de» va en
  // minúscula. En inglés no se nota porque los meses ya van en mayúscula, que
  // es justo por lo que un error así sobrevive a la revisión.
  const crudo = new Intl.DateTimeFormat(local, { month: 'long', year: 'numeric' })
    .format(new Date(anio, mesN - 1, 15));
  const mes = crudo.charAt(0).toUpperCase() + crudo.slice(1);

  const importe = (v: string): Importe => ({ valor: v, moneda });

  return (
    <>
      <div className="page-head">
        <div className="page-head__text">
          <h1>{mes}</h1>
          <p className="page-head__sub">
            {resumen ? (
              <Texto k="dash.sub" con={{
                dias: String(dias), asientos: String(resumen.asientos),
              }} />
            ) : t('common.loading')}
          </p>
        </div>
      </div>

      {fallo && (
        <div className="alert alert--warn" style={{ marginBottom: 'var(--s6)' }}>
          <span>{t(fallo as never)}</span>
        </div>
      )}

      {/* ▶ Lo primero que ve quien entra, y solo mientras haga falta.
       *
       * Va ARRIBA de las tarjetas a propósito: un tablero en ceros no dice si
       * es porque falta la apertura o porque así se ve una empresa nueva, y
       * quien entra el primer día no tiene cómo distinguirlo. Se quita solo
       * cuando los cinco pasos están. */}
      <PuestaEnMarcha />

      {/* ▶ Un mes SIN ASIENTOS no se compara con nada.
       *
       * Septiembre recién empezado, con cero asientos, salía diciendo «▼ 100 %»
       * en las tres tarjetas. Es aritméticamente cierto —cero contra el mes
       * pasado— y se lee como que el negocio se cayó, cuando lo que pasa es que
       * todavía no se ha registrado nada. La cabecera ya dice «0 asientos»; la
       * variación se calla hasta que haya algo que variar. */}
      {resumen && (
        <section className="grid grid--kpi" style={{ marginBottom: 'var(--s6)' }}>
          <Kpi
            rotulo={t('dash.kpi.income')} valor={importe(resumen.ingresos)}
            tono=" kpi__value--pos"
            delta={variacion(resumen.ingresos, resumen.ingresos_previo)}
            mesAnterior={comparable ? mesAnterior : null}
          />
          <Kpi
            rotulo={t('dash.kpi.expense')} valor={importe(resumen.gastos)}
            tono=" kpi__value--neg"
            delta={variacion(resumen.gastos, resumen.gastos_previo)}
            mesAnterior={comparable ? mesAnterior : null}
          />
          <Kpi
            rotulo={t('dash.kpi.profit')} valor={importe(resumen.utilidad)}
            delta={variacion(resumen.utilidad, resumen.utilidad_previo)}
            mesAnterior={comparable ? mesAnterior : null}
          />
          {/* ▶▶ LA CUARTA TARJETA, QUE ESTUVO SIN NÚMERO DESDE LA FASE 0.
           *
           * Decía el motivo en vez de una cifra: ««efectivo y equivalentes»
           * necesita saber cuáles cuentas lo son, y el catálogo no lo declara».
           * Ya lo declara —la `0100` le puso `flujo` a cada cuenta de balance,
           * y `efectivo` es uno de sus valores—, así que la tarjeta tiene
           * número y **no hubo que decidir nada nuevo para dárselo**.
           *
           * ▶ Y esta tarjeta compara dos SALDOS, no dos flujos del mes como sus
           * tres vecinas: el efectivo al cierre de este mes contra el del
           * anterior. Es la comparación que significa algo para una caja — la
           * variación del mes ya la cuenta, línea por línea, el estado de flujos.
           *
           * ▶ `hay_efectivo` en falso NO es un saldo en cero. Cero con cuentas
           * declaradas es que la empresa se quedó sin caja; cero sin ellas es que
           * nadie sabe cuánta tiene. La segunda sigue enseñando el motivo. */}
          <Kpi
            rotulo={t('dash.kpi.cash')}
            valor={resumen.hay_efectivo && resumen.efectivo !== null
              ? importe(resumen.efectivo) : null}
            delta={variacion(resumen.efectivo, resumen.efectivo_previo)}
            mesAnterior={comparable ? mesAnterior : null}
            sinDatos={t('dash.cashUndeclared')}
          />
        </section>
      )}

      {/* Los índices, contra su meta. */}
      <PanelDeIndices anio={anio} mes={mesN} />

      <section className="grid grid--main">
        <Grafica serie={serie} moneda={moneda} anio={anio} />
        <div className="stack">
          {resumen && <EcuacionContable resumen={resumen} moneda={moneda} />}
          <ExposicionCambiaria expuestas={expuestas} moneda={moneda} />
        </div>
      </section>

      {/* ▶ El saldo de caja y bancos, de un vistazo. No depende del periodo del
          tablero: es un corte a la fecha que se elija (por defecto, hoy). */}
      <div style={{ marginTop: 'var(--s6)' }}>
        <Saldos conFecha />
      </div>

      {!cargando && !resumen && !fallo && (
        <p className="muted small" style={{ marginTop: 'var(--s6)' }}>{t('dash.noPeriod')}</p>
      )}
    </>
  );
}
